'use client'

import {
  Block16,
  CheckCircle16,
  CheckmarkShield16,
  Clock16,
  Document16,
  Envelope16,
  Flag16,
  HorizontalDots24,
  LocationPin16,
  Member16,
  Pencil24,
  Rss16,
  X16,
} from '@vinted/monochrome-icons'
import { Button, Card, Cell, Divider, Icon, Image, Rating, Spacer, Text } from '@vinted/web-ui'
import { compact, isEmpty, noop } from 'lodash'
import { ReactNode, useRef, useState } from 'react'
import { v4 as uuid } from 'uuid'

import { businessAccountFromUserDto } from '@marketplace-web/business-accounts/business-accounts-data'
import { FundraiserInfo } from '@marketplace-web/donations/fundraisers-feature'
import { LinkifiedMessage } from '@marketplace-web/linkified-message/linkified-message-feature'
import { SellerBadge } from '@marketplace-web/seller-engagement-tools/seller-engagement-tools-feature'
import { SellerBadgeDto } from '@marketplace-web/seller-engagement-tools/seller-engagement-tools-data'
import {
  HideAtBreakpoint,
  ShowAtBreakpoint,
  useBreakpoint,
} from '@marketplace-web/breakpoints/breakpoints-feature'
import {
  useBrowserNavigation,
  reloadPage,
  navigateToPage,
} from '@marketplace-web/browser/browser-navigation-util'
import { setSessionStorageItem } from '@marketplace-web/browser/browser-storage-util'
import { useTracking } from '@marketplace-web/observability/event-tracker-data'
import { useFeatureSwitch } from '@marketplace-web/feature-flags/feature-switches-data'
import {
  FormattedDateMessage,
  FormattedMessage,
  useTranslate,
} from '@marketplace-web/i18n/i18n-feature'
import { Translation } from '@marketplace-web/i18n/i18n-data'
import { useSession } from '@marketplace-web/shared/session-data'
import { EMPTY_USER_IMAGE_NAME, useAsset } from '@marketplace-web/shared/assets'
import { SeparatedList } from '@marketplace-web/common-components/separated-list-ui'
import { UserTitle } from '@marketplace-web/common-components/user-title-ui'
import { urlWithParams } from '@marketplace-web/browser/url-util'
import { REPORT_SESSION_ID_KEY, AlertType } from '@marketplace-web/admin-alert/admin-alert-data'
import {
  clickEvent,
  followUserEvent,
  blockUser,
  getUserById,
  unblockUser,
  ProfileUserDto,
} from '@marketplace-web/profile/profile-data'
import DropdownMenu from '@marketplace-web/profile/dropdown-menu-ui'
import FollowButton from '@marketplace-web/user-follow/follow-button-feature'
import { TranslationControl } from '@marketplace-web/i18n/translations-feature'
import { UserBlockModal } from '@marketplace-web/conversation-safety/conversation-safety-feature'
import { UnauthenticatedReportModal } from '@marketplace-web/personal-information/personal-information-feature'

import { USER_STATUS_MEMBER_DELETED, VERIFICATION_LIST } from '../../../constants'
import {
  MEMBER_RESTORE_URL,
  MEMBER_TEMPLATES_URL,
  NEW_ADMIN_ALERT_URL,
  PROFILE_SETTINGS_URL,
} from '../../../constants/routes'
import { calculateRating, getIsAccountBanPermanent } from '../../../utils/user'
import FollowInfo from '../FollowInfo'
import UserInfoBlock from '../InfoHeader/UserInfoBlock'
import ProfileInfoBlock from './ProfileInfoBlock'
import { isDateInTheFuture } from '../../../utils/date'

type Content = {
  about: string
}

const renderWarningBlock = (key: string, text: ReactNode, enableHtml?: boolean) => {
  const data = {
    text,
    enableHtml,
    iconName: key === 'deleted' ? X16 : Block16,
    theme: 'warning' as const,
    color: 'warning-default' as const,
  }

  return <ProfileInfoBlock key={key} useFlexbox data={data} />
}

const renderInfoBlock = (
  key: string,
  text: string | JSX.Element,
  iconName: React.ComponentProps<typeof Icon>['name'],
  color?: React.ComponentProps<typeof Icon>['color'],
) => {
  const data = { key, text, iconName, color }

  return <ProfileInfoBlock key={key} useFlexbox data={data} />
}

type Props = {
  compactMode?: boolean
  onUserInfoClick?: () => void
  onTogglePoliciesModal?: () => void
  onAboutClick?: () => void
  user: ProfileUserDto & {
    seller_badges?: Array<Omit<SellerBadgeDto, 'progress'>>
  }
  sellerBadgesList: Array<Omit<SellerBadgeDto, 'progress'>> | undefined
  screen: string
  viewingSelf: boolean
  showActions: boolean
  isShadowBanned?: boolean
  showSupportFunctionality: boolean
  canSendTemplates: boolean
  isContentTranslationEnabled: boolean
}

const ProfileInfo = ({
  compactMode = false,
  onUserInfoClick,
  user,
  sellerBadgesList,
  screen,
  viewingSelf = false,
  showActions = true,
  isShadowBanned = false,
  showSupportFunctionality,
  canSendTemplates,
  isContentTranslationEnabled,
  onTogglePoliciesModal = noop,
  onAboutClick,
}: Props) => {
  const { relativeUrl } = useBrowserNavigation()
  const translate = useTranslate('user')
  const businessTranslate = useTranslate()
  const { track } = useTracking()
  const asset = useAsset('assets/no-photo')
  const breakpoints = useBreakpoint()

  const [isContentTranslated, setIsContentTranslated] = useState(false)
  const [translatedContent, setTranslatedContent] = useState<Content | null>(null)
  const [isBlockModalVisible, setIsBlockModalVisible] = useState(false)
  const [isReportModalVisible, setIsReportModalVisible] = useState(false)
  const isFollowTracked = useRef(false)

  const isProfileReportingModalEnabled = useFeatureSwitch('ntd_form_user')
  const isProfileBlockingImprovementsEnabled = useFeatureSwitch('profile_blocking_improvements')
  const isUKCompanyNumberHidden = useFeatureSwitch('hide_uk_sole_traders_company_number')

  const { user: currentUser } = useSession()
  const businessAccount = businessAccountFromUserDto(user)

  const { business: isBusinessAccount } = user

  async function translateDescription() {
    if (translatedContent) {
      setIsContentTranslated(true)

      return
    }

    const response = await getUserById({ id: user.id, localize: true })

    if ('errors' in response) return

    const { about } = response.user

    if (!about) return

    setTranslatedContent({ about })
    setIsContentTranslated(true)
  }

  function showOriginal() {
    setIsContentTranslated(false)
  }

  function handleTranslate(isTranslated: boolean) {
    return isTranslated ? showOriginal() : translateDescription()
  }

  const toggleBlock = async () => {
    if (!currentUser) return

    const toggleFunction = user.is_hated ? unblockUser : blockUser

    await toggleFunction({ currentUserId: currentUser.id, blockedUserId: user.id })
    reloadPage()
  }

  function handleBlockCancel() {
    setIsBlockModalVisible(false)
  }

  function handleModalBlockButtonClick() {
    setIsBlockModalVisible(false)
    reloadPage()
  }

  function handleBlockClick() {
    if (isProfileBlockingImprovementsEnabled) {
      setIsBlockModalVisible(true)

      return
    }

    toggleBlock()
  }

  function handleReportClick() {
    if (currentUser || !isProfileReportingModalEnabled) {
      const url = urlWithParams(NEW_ADMIN_ALERT_URL, {
        ref_id: user.id,
        ref_type: AlertType.Member,
        ref_url: relativeUrl,
      })

      setSessionStorageItem(REPORT_SESSION_ID_KEY, uuid())

      track(
        clickEvent({
          screen: 'user_profile',
          target: 'report_profile',
          targetDetails: String(user.id),
        }),
      )

      navigateToPage(url)

      return
    }

    setIsReportModalVisible(true)
  }

  function handleReportModalClose() {
    setIsReportModalVisible(false)
  }

  function renderProfileImage() {
    const photoUrl = user.photo ? user.photo.url : asset(EMPTY_USER_IMAGE_NAME)

    return <Image src={photoUrl} scaling="fill" />
  }

  function getRestrictionTranslationKey(key: string, isRestrictionPermanent: boolean) {
    const isDateVisibleForUser = viewingSelf || showSupportFunctionality || user.is_god

    return isDateVisibleForUser && !isRestrictionPermanent ? key : `${key}_no_date`
  }

  function renderInfoHeader() {
    const { localization, id } = user
    const activeContent = isContentTranslated && translatedContent ? translatedContent : user

    const showTranslationControl =
      isContentTranslationEnabled && localization && localization !== Translation.None

    const showUserActions = showSupportFunctionality || (!viewingSelf && !user.moderator)

    const toggleBlockText = user.is_hated ? translate('action.unblock') : translate('action.block')

    const actions = [
      {
        id: 'report',
        text: translate('action.report'),
        onClick: handleReportClick,
      },
      { id: user.is_hated ? 'unblock' : 'block', text: toggleBlockText, onClick: handleBlockClick },
    ]

    const dropdownButtonProps: React.ComponentProps<typeof Button> = {
      styling: 'flat',
      icon: <Icon name={HorizontalDots24} color="greyscale-level-2" testId="actions-menu-button" />,
      size: 'medium',
    }

    const aboutText = activeContent?.about ?? ''

    return (
      <ProfileInfoBlock>
        <Cell>
          <div className="u-flexbox u-justify-content-between">
            <Text
              as="h1"
              type="heading"
              truncate
              text={
                <UserTitle
                  badgeTitle={businessTranslate('business.title')}
                  businessAccountName={businessAccount?.name}
                  login={user.login}
                  isBusiness={user.business}
                  isBadgeHidden
                  isLoginForced
                />
              }
            />
            {showUserActions && (
              <div>
                <DropdownMenu actions={actions} buttonProps={dropdownButtonProps} />
              </div>
            )}
          </div>
          {aboutText && (
            <>
              <Text as="span" format>
                <LinkifiedMessage
                  text={aboutText}
                  trackingData={{
                    user_id: user.id.toString(),
                  }}
                />
              </Text>
              {showTranslationControl && (
                <>
                  <Spacer />
                  <TranslationControl
                    isTranslated={isContentTranslated}
                    autotranslate={false}
                    onClick={handleTranslate}
                    tracking={{
                      target: 'translate_description',
                      targetDetails: id.toString(),
                    }}
                  />
                </>
              )}
            </>
          )}
        </Cell>
      </ProfileInfoBlock>
    )
  }

  function renderBlockedWarningBlock() {
    return (
      <ProfileInfoBlock key="blocked-warning">
        <Cell>
          <UserInfoBlock iconName={Block16} iconColor="warning-default">
            <Text as="span" theme="warning">
              {translate('admin.messages.blocked')}
            </Text>
          </UserInfoBlock>
          {isProfileBlockingImprovementsEnabled && (
            <>
              <Spacer size="large" />
              <Button
                text={translate('action.unblock')}
                styling="filled"
                onClick={handleBlockClick}
              />
            </>
          )}
        </Cell>
      </ProfileInfoBlock>
    )
  }

  function renderSellerPolicies() {
    if (!isBusinessAccount) return null

    return (
      <UserInfoBlock iconName={Document16}>
        <button type="button" onClick={onTogglePoliciesModal}>
          <Text clickable text={translate('seller_policies.title')} as="span" />
        </button>
      </UserInfoBlock>
    )
  }

  function renderWarningParts() {
    const { is_hated, account_ban_date, account_status, id, is_account_banned } = user

    const showDeleted = account_status === USER_STATUS_MEMBER_DELETED
    const isAccountBanPermanent = getIsAccountBanPermanent(account_ban_date)

    const parts = compact([
      !is_hated &&
        is_account_banned &&
        renderWarningBlock(
          'banned',
          translate(getRestrictionTranslationKey('admin.messages.banned', isAccountBanPermanent), {
            date: account_ban_date,
          }),
        ),
      is_hated && renderBlockedWarningBlock(),
      isShadowBanned &&
        renderWarningBlock('on_hold_no_date', translate('admin.messages.on_hold_no_date')),
      showDeleted &&
        renderWarningBlock(
          'deleted',
          translate('admin.messages.deleted', {
            'member-restore-url': chunks => <a href={MEMBER_RESTORE_URL(id)}>{chunks}</a>,
          }),
          true,
        ),
    ])

    if (isEmpty(parts)) return null

    return parts
  }

  function renderFeedback() {
    const { feedback_reputation, feedback_count } = user
    const rating = calculateRating(feedback_reputation)

    return (
      <ProfileInfoBlock>
        <Cell type="navigating" onClick={onUserInfoClick} chevron>
          <Rating
            aria={{
              'aria-label': (rating && translate('a11y.rating', { rating, max_rating: 5 })) || '',
            }}
            value={rating}
            text={translate(
              'feedback.reviews',
              { count: feedback_count },
              { count: feedback_count },
            )}
            emptyStateText={translate('feedback.no_reviews')}
          />
        </Cell>
      </ProfileInfoBlock>
    )
  }

  function renderDonation() {
    if (!user.fundraiser?.active) return null
    if (user.fundraiser.feature_disabled) return null

    return (
      <ProfileInfoBlock>
        <FundraiserInfo user={user} screen={screen} />
      </ProfileInfoBlock>
    )
  }

  function renderMemberStatuses() {
    const { moderator, volunteer_moderator, is_identity } = user

    const parts = compact([
      moderator &&
        renderInfoBlock(
          'product_team',
          translate('status.product_team'),
          CheckmarkShield16,
          'primary-default',
        ),
      volunteer_moderator &&
        renderInfoBlock(
          'volunteer_moderator',
          translate('status.volunteer_moderator'),
          CheckmarkShield16,
          'primary-default',
        ),
      is_identity &&
        renderInfoBlock(
          'identity_confirmed',
          translate('status.identity_confirmed'),
          CheckCircle16,
          'success-default',
        ),
    ])

    if (isEmpty(parts)) return null

    return parts
  }

  function renderBusinessEmail() {
    const hideBusinessEmail = isBusinessAccount && businessAccount?.personal_email
    const businessEmail = isBusinessAccount && businessAccount?.email

    if (!businessEmail) return null

    return (
      <UserInfoBlock iconName={Envelope16}>
        {hideBusinessEmail ? (
          <span>{businessEmail}</span>
        ) : (
          <a href={`mailto:${businessEmail}`}>{businessEmail}</a>
        )}
      </UserInfoBlock>
    )
  }

  function renderBusinessPhone() {
    const hideBusinessPhoneNumber = isBusinessAccount && businessAccount?.personal_phone_number
    const businessPhone = isBusinessAccount && businessAccount?.phone_number

    if (!businessPhone) return null

    return (
      <UserInfoBlock iconName={Envelope16}>
        {hideBusinessPhoneNumber ? (
          <span>{businessPhone}</span>
        ) : (
          <a href={`tel:${businessPhone}`}>{businessPhone}</a>
        )}
      </UserInfoBlock>
    )
  }

  function renderBusinessVerifications() {
    if (!isBusinessAccount) return null

    const businessLegalCode = businessAccount?.legal_code
    const hideBusinessLegalCode =
      isUKCompanyNumberHidden &&
      businessAccount?.country === 'UK' &&
      businessAccount.entity_type === 'sole_trader'
    const businessRegistrarName = businessAccount?.registrar_name
    const selfCertified = businessAccount?.verified_identity

    return (
      <>
        <UserInfoBlock iconName={CheckCircle16}>
          {translate('verification.business_identity')}
        </UserInfoBlock>

        {businessLegalCode && !hideBusinessLegalCode && (
          <UserInfoBlock iconName={CheckCircle16}>
            {translate('verification.legal_code')} {businessLegalCode}
          </UserInfoBlock>
        )}

        {businessRegistrarName && (
          <UserInfoBlock iconName={CheckCircle16} testId="business-verification-registrar-name">
            {businessRegistrarName}
          </UserInfoBlock>
        )}

        {selfCertified && (
          <UserInfoBlock iconName={CheckCircle16} testId="business-verification-self-certified">
            {translate('verification.complies_with_eu_law')}
          </UserInfoBlock>
        )}
      </>
    )
  }

  function renderVerifications() {
    const filteredVerifications = VERIFICATION_LIST.filter(item => user.verification[item]?.valid)

    if (isEmpty(filteredVerifications) && !isBusinessAccount) return null

    if (!compactMode) {
      return (
        <ProfileInfoBlock useFlexbox>
          <div>
            <Text as="h4" type="caption" text={<FormattedMessage id="user.verification.title" />} />
            {filteredVerifications.map(verification => (
              <UserInfoBlock key={verification} iconName={CheckCircle16}>
                {translate(`verification.${verification}`)}
              </UserInfoBlock>
            ))}
            {renderBusinessVerifications()}
          </div>
        </ProfileInfoBlock>
      )
    }

    return (
      <UserInfoBlock iconName={CheckCircle16}>
        {filteredVerifications
          .map(item => translate(`verification.${item}`))
          .concat(isBusinessAccount ? [translate('verification.business_identity')] : [])
          .join(', ')}
      </UserInfoBlock>
    )
  }

  const renderLocationContent = () => {
    const { expose_location, city, country_title_local } = user

    const hideBusinessLocation = isBusinessAccount && businessAccount?.entity_type === 'sole_trader'
    const businessAddress = isBusinessAccount && businessAccount?.address
    const locationDescription =
      expose_location && city ? [city, country_title_local].join(', ') : country_title_local

    if (locationDescription && !isBusinessAccount) {
      return locationDescription
    }

    if (businessAddress && hideBusinessLocation) {
      const businessLocationDescription =
        expose_location && city
          ? [businessAddress.city, businessAddress.country_title].join(', ')
          : businessAddress.country_title

      return businessLocationDescription
    }

    if (businessAddress && !hideBusinessLocation) {
      return (
        <>
          {businessAddress?.line1 && <div>{businessAddress.line1}</div>}
          {businessAddress?.line2 && <div>{businessAddress.line2}</div>}
          {businessAddress?.postal_code && businessAddress?.city && (
            <div>
              {businessAddress.postal_code} {businessAddress.city}
            </div>
          )}
          {businessAddress?.country_title && <div>{businessAddress.country_title}</div>}
        </>
      )
    }

    return null
  }

  const renderLocationBlock = () => {
    if (!renderLocationContent()) {
      return null
    }

    return (
      <UserInfoBlock iconName={LocationPin16} testId="profile-location-info">
        {renderLocationContent()}
      </UserInfoBlock>
    )
  }

  const trackFollowToggle = ({ isFollowEvent }: { isFollowEvent: boolean }) => {
    track(followUserEvent({ userId: user.id, isFollowEvent }))

    if (isFollowTracked.current) return

    isFollowTracked.current = true
    track(clickEvent({ target: 'follow', screen: 'user_profile' }))
  }

  const renderFollowMessage = () => {
    const { followers_count, following_count, id } = user

    return (
      <FollowInfo
        followersCount={followers_count}
        followingCount={following_count}
        userId={id}
        viewingSelf={viewingSelf}
      />
    )
  }

  const renderAdditionalInfo = () => {
    const { email, last_loged_on_ts, created_at, msg_template_count } = user

    const memberSinceDate = new Date(created_at)

    return (
      <ProfileInfoBlock useFlexbox>
        <div>
          {renderLocationBlock()}
          {renderBusinessEmail()}
          {renderBusinessPhone()}

          {last_loged_on_ts && (
            <UserInfoBlock iconName={Clock16}>
              <Text as="span">
                <FormattedDateMessage
                  id="user.last_login"
                  date={new Date(last_loged_on_ts)}
                  relativeDate
                />
              </Text>
            </UserInfoBlock>
          )}

          <UserInfoBlock iconName={Rss16}>{renderFollowMessage()}</UserInfoBlock>
          {showSupportFunctionality && (
            <UserInfoBlock iconName={Member16}>
              <Text as="span">
                <FormattedDateMessage id="user.since" date={memberSinceDate} relativeDate={false} />
              </Text>
            </UserInfoBlock>
          )}
          {showSupportFunctionality && (
            <UserInfoBlock iconName={Envelope16}>
              <Text as="span" text={email} />
            </UserInfoBlock>
          )}
          {canSendTemplates && (
            <UserInfoBlock iconName={Flag16}>
              <a href={MEMBER_TEMPLATES_URL(user.id)}>
                {translate(
                  'admin.template_count',
                  { count: msg_template_count },
                  { count: msg_template_count },
                )}
              </a>
            </UserInfoBlock>
          )}
          {renderSellerPolicies()}
        </div>
      </ProfileInfoBlock>
    )
  }

  const renderTopActions = () => {
    if (!viewingSelf) return null

    return (
      <ProfileInfoBlock useFlexbox>
        <Button
          icon={<Icon name={Pencil24} color="greyscale-level-2" />}
          url={PROFILE_SETTINGS_URL}
          text={translate('action.edit_profile')}
          theme="muted"
          size="medium"
        />
      </ProfileInfoBlock>
    )
  }

  const renderBottomActions = () => {
    const showFollowButton =
      showActions && !viewingSelf && !isDateInTheFuture(user.closet_countdown_end_date)

    if (user.is_account_banned || !showFollowButton) return null

    return (
      <ProfileInfoBlock useFlexbox>
        <FollowButton
          onFollowToggle={trackFollowToggle}
          userId={user.id}
          isActive={user.is_favourite}
          size="medium"
          testId="profile-info-follow-button"
        />
      </ProfileInfoBlock>
    )
  }

  const renderCompactUserInfo = () => {
    const { feedback_reputation, photo, feedback_count } = user
    const rating = calculateRating(feedback_reputation)
    const photoUrl = photo ? photo.url : asset(EMPTY_USER_IMAGE_NAME)

    const infoProps = {
      prefix: <Image src={photoUrl} styling="circle" size="large" />,
      title: (
        <UserTitle
          badgeTitle={businessTranslate('business.title')}
          businessAccountName={businessAccount?.name}
          login={user.login}
          isBusiness={user.business}
        />
      ),
      body: (
        <>
          <Rating
            aria={{
              'aria-label': (rating && translate('a11y.rating', { rating, max_rating: 5 })) || '',
            }}
            value={rating}
            text={feedback_count}
            emptyStateText={translate('feedback.no_reviews')}
          />
          <Spacer size="small" />
        </>
      ),
      type: 'navigating' as const,
      onClick: onUserInfoClick,
      chevron: true,
      id: 'profile-compact-info',
    }

    return <Cell {...infoProps} />
  }

  const renderCompactAdditionalInfo = () => {
    const shouldBeNavigating = compactMode && breakpoints.phones && onAboutClick

    const cellProps: React.ComponentProps<typeof Cell> = shouldBeNavigating
      ? {
          type: 'navigating',
          onClick: onAboutClick,
          chevron: true,
        }
      : {}

    return (
      <Cell {...cellProps} testId="compact-additional-info-cell">
        {renderVerifications()}
        {renderLocationBlock()}
        {renderBusinessEmail()}
        <UserInfoBlock iconName={Rss16}>{renderFollowMessage()}</UserInfoBlock>
      </Cell>
    )
  }

  const renderMainInfo = () => {
    return (
      <SeparatedList separator={<Divider />}>
        {renderTopActions()}
        {renderInfoHeader()}
        {renderWarningParts()}
        {renderDonation()}
        {renderFeedback()}
        {renderMemberStatuses()}
        {renderVerifications()}
        {renderAdditionalInfo()}
        {renderBottomActions()}
      </SeparatedList>
    )
  }

  const renderUserBlockModal = () => {
    return (
      <UserBlockModal
        username={user.login}
        isUserBlocked={user.is_hated}
        userId={user.id}
        isVisible={isBlockModalVisible}
        onCancel={handleBlockCancel}
        onConfirm={handleModalBlockButtonClick}
      />
    )
  }

  const renderSellerBadges = () => {
    if (!sellerBadgesList?.length) return null

    return (
      <Cell>
        <div className="u-flexbox u-flex-direction-column">
          <SeparatedList separator={<Spacer size="large" />}>
            {sellerBadgesList.map((badge, index) => (
              <SellerBadge type={badge.type} key={index} />
            ))}
          </SeparatedList>
        </div>
      </Cell>
    )
  }

  if (compactMode) {
    return (
      <SeparatedList separator={<Divider />}>
        {renderCompactUserInfo()}
        {renderDonation()}
        {renderSellerBadges()}
        {renderCompactAdditionalInfo()}
        {renderBottomActions()}
      </SeparatedList>
    )
  }

  return (
    <>
      <HideAtBreakpoint breakpoint="tablets">
        <Card>
          <div className="u-overflow-hidden">
            {renderProfileImage()}
            {renderMainInfo()}
          </div>
        </Card>
      </HideAtBreakpoint>
      <ShowAtBreakpoint breakpoint="tablets">
        <Spacer size="large" />
        <div className="u-flexbox">
          <Spacer orientation="vertical" size="large" />
          <Card>
            <div className="u-overflow-hidden">{renderProfileImage()}</div>
          </Card>
          <Spacer orientation="vertical" size="large" />
          <div className="u-flex-grow">
            <Card>
              <div className="u-overflow-hidden">{renderMainInfo()}</div>
            </Card>
          </div>
          <Spacer orientation="vertical" size="large" />
        </div>
      </ShowAtBreakpoint>
      <UnauthenticatedReportModal
        isVisible={isReportModalVisible}
        onClose={handleReportModalClose}
      />
      {renderUserBlockModal()}
    </>
  )
}

export default ProfileInfo
